import React, { memo, useMemo } from 'react';

import { AppHeader, PageTitleContainer } from './components/StyledComponents';
import { ErrorBoundary, Flex, InlineErrorFallback, Label } from '@zeal/web-ui';

import NavBarAvatar from './components/NavBarAvatar';
import OwnAppSwitcher from './components/OwnAppSwitcher';
import DashboardLogo from './components/DashboardLogo';
import UpgradeButton from './components/UpgradeButton';
import { useLoggedIn } from '@app/ACL/useLogin';
import { BannerAlertIcon } from './components/Banners';
import { EMerchantType } from '@app/store/authStore';
import { BannerState } from './components/Banners';
import { usePageStore } from '@app/store/pageStore';
import { useTranslation } from 'react-i18next';
import Notifications from './components/Notifications/Notifications';

type NavBarProps = {
	routeLabel?: string;
	bannerState: BannerState;
	setIsBannerVisible: (visible: boolean) => void;
};

function NavBar({ routeLabel, bannerState, setIsBannerVisible }: NavBarProps) {
	const { merchantType } = useLoggedIn();
	const { pageConfig } = usePageStore();

	const { t } = useTranslation('sidedrawer');

	const appHeaderEndContent = useMemo(
		() => (
			<Flex gap="sm" justify="end" align="center" className="overflow-hidden">
				<BannerAlertIcon
					type={bannerState.type}
					onClick={() => setIsBannerVisible(true)}
				/>
				{merchantType === EMerchantType.Freemium && <UpgradeButton />}
				{merchantType === EMerchantType.Premium && <Notifications />}
				<NavBarAvatar />
				<OwnAppSwitcher />
			</Flex>
		),
		[bannerState.type, merchantType, setIsBannerVisible]
	);

	const title = routeLabel ? t(routeLabel) : '';

	const pageTitleComp = useMemo(() => {
		return pageConfig?.title || title;
	}, [pageConfig?.title, title]);

	const pageTitle = useMemo(
		() =>
			pageTitleComp ? (
				<Label.Mid400 id="pageTitle">{pageTitleComp}</Label.Mid400>
			) : null,
		[pageTitleComp]
	);

	return (
		<ErrorBoundary
			fallback={<InlineErrorFallback style={{ gridArea: 'breadcrumbs' }} />}
		>
			<AppHeader tabs={pageConfig.middleContent}>
				<DashboardLogo />
				<PageTitleContainer>
					<span />
					{pageTitle || <span />}
				</PageTitleContainer>
				{appHeaderEndContent}
			</AppHeader>
		</ErrorBoundary>
	);
}

export default memo(NavBar);
